import { type DependencyList, useEffect, useRef } from 'react';

/**
 * Like useEffect, but with built-in `once` semantics for one-shot tracking.
 *
 * ### `once` option
 * When `once: true`, the hook fires at most once per component lifetime.
 * The effect should return `false` to signal "not ready yet" — the hook will
 * retry on subsequent dep changes without consuming the once budget.
 * Any other return value (including void/undefined) marks the effect as done.
 *
 * @example — basic (re-fires when deps change)
 * usePicoEffect(() => {
 *   trackSomething({ query });
 * }, [query]);
 *
 * @example — once (fires once, retries if effect returns false)
 * usePicoEffect(() => {
 *   if (!data) return false; // not ready, try again when deps change
 *   trackPageDisplayed({ id: data.id });
 * }, [data], { once: true });
 */
export function usePicoEffect(
  effect: () => boolean | void,
  deps: DependencyList,
  options?: { once?: boolean },
): void {
  const effectRef = useRef(effect);
  effectRef.current = effect;

  const once = options?.once ?? false;
  const firedRef = useRef(false);

  useEffect(
    () => {
      if (once && firedRef.current) return;
      const result = effectRef.current();
      if (once && result !== false) {
        firedRef.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps],
  );
}
